import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { CreateAppointmentDtoSchema, TimeManagementListItem } from '@myapp/contract'
import { useDispositionDraftStore } from '../model/useDispositionDraftStore'
import { useCollisionDetection } from '../../../shared/lib/collision'
import { useCollisionAwareDurations } from '../model/useCollisionAwareDurations'
import { buildTimedTasksByTechnician } from '../model/buildTimedTasksByTechnician'
import { useTechnicianAppointments } from '../../../entities/appointment'
import { useDeleteBreak, useBreakBlocks } from '../../../entities/time-entry'
import { resolveBreakForDay } from '../../../entities/time-entry/model/useBreakBlock'
import { useTimelineTasks } from '../../../entities/task'
import { getDayRange } from '../../../shared/lib/blockRanges'
import { filterSchedulableTimeManagement } from '../../../shared/lib/timeline/weekHelpers'
import { DraftAppointmentBar } from './DraftAppointmentBar'
import { DraftAppointmentForm } from './DraftAppointmentForm'

interface Props {
  onSave: () => void
  onCancel: () => void
  isPending: boolean
  mode?: 'create' | 'edit'
  files: File[]
  onFilesChange: (files: File[]) => void
  existingAttachments: string[]
  onRemoveExistingAttachment: (index: number) => void
  backendError?: string | null
}

function formatWorkDateTime(iso: string) {
  const d = new Date(iso)
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return { date, time }
}

export const DraftAppointmentPanel: React.FC<Props> = ({
  onSave,
  onCancel,
  isPending,
  mode = 'create',
  files,
  onFilesChange,
  existingAttachments,
  onRemoveExistingAttachment,
  backendError,
}) => {
  const draft = useDispositionDraftStore((s) => s.draft)
  const technicianName = useDispositionDraftStore((s) => s.technicianName)
  const ghost = useDispositionDraftStore((s) => s.ghost)
  const setDurations = useDispositionDraftStore((s) => s.setDurations)
  const updateTradeCode = useDispositionDraftStore((s) => s.updateTradeCode)
  const updateDescription = useDispositionDraftStore((s) => s.updateDescription)
  const updatePosition = useDispositionDraftStore((s) => s.updatePosition)
  const isFullDay = useDispositionDraftStore((s) => s.isFullDay)
  const setFullDay = useDispositionDraftStore((s) => s.setFullDay)
  const isGhostInvalid = useDispositionDraftStore((s) => s.isGhostInvalid)

  // Derive dayIso from ghost position — avoids widget-layer dependency
  const anfahrtIso = useDispositionDraftStore((s) => s.ghost?.anfahrtStartIso)
  const ghostTechnicianId = useDispositionDraftStore((s) => s.ghost?.technicianId)
  const range = getDayRange(anfahrtIso ?? new Date().toISOString())
  const dayIso = anfahrtIso ? anfahrtIso.slice(0, 10) : ''

  const { data } = useTechnicianAppointments({ from: range.from, to: range.to })
  const taskItems = useTimelineTasks(range.from, range.to)

  const breakBlocks = useBreakBlocks(data?.technicians, dayIso)

  const timedTasksByTechnician = useMemo(() => buildTimedTasksByTechnician(taskItems), [taskItems])

  const timeManagementByTechnician = useMemo(() => {
    const map = new Map<string, TimeManagementListItem[]>()
    if (!data?.technicians) return map
    for (const tech of data.technicians) {
      map.set(tech.technicianId, filterSchedulableTimeManagement(tech.timeManagementEntries ?? []))
    }
    return map
  }, [data?.technicians])

  const { hasCollision } = useCollisionDetection(
    data?.technicians, breakBlocks, timedTasksByTechnician, timeManagementByTechnician, dayIso,
  )

  const technicianShift = data?.technicians.find((t) => t.technicianId === ghostTechnicianId) ?? null

  const deleteBreak = useDeleteBreak()

  // Delete break override for a technician on the current day (if one exists)
  const deleteBreakForTechnician = useCallback((techId: string) => {
    const tech = data?.technicians.find((t) => t.technicianId === techId)
    if (!tech || !dayIso) return
    const resolved = resolveBreakForDay(tech, dayIso)
    if (resolved.overrideId != null) {
      deleteBreak.mutate(resolved.overrideId)
    }
  }, [data?.technicians, dayIso, deleteBreak])

  // When full-day is active and the ghost technician changes (drag to another row),
  // delete the new technician's break override for that day
  const prevTechIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isFullDay || !ghostTechnicianId) {
      prevTechIdRef.current = ghostTechnicianId ?? null
      return
    }
    if (ghostTechnicianId !== prevTechIdRef.current) {
      deleteBreakForTechnician(ghostTechnicianId)
      prevTechIdRef.current = ghostTechnicianId
    }
  }, [isFullDay, ghostTechnicianId, deleteBreakForTechnician])

  const { tryDurationsChange, collisionWarning } = useCollisionAwareDurations({
    draft,
    ghost,
    hasCollision,
    setDurations,
  })

  const [showModal, setShowModal] = useState(false)

  if (!draft) return null

  const draftAsDto = {
    workOrderId: draft.workOrderId,
    assignedTechnicianId: draft.assignedTechnicianId,
    plannedStart: draft.date,
    tradeCode: draft.tradeCode,
    plannedTravelDuration: draft.plannedTravelDuration,
    plannedWorkDuration: draft.plannedWorkDuration,
    plannedDepartureDuration: draft.plannedDepartureDuration,
    attachments: null,
  }

  const parsed = CreateAppointmentDtoSchema.safeParse(draftAsDto)
  const canSave = parsed.success && !isGhostInvalid
  const workDateTime = draft.date ? formatWorkDateTime(draft.date) : null
  const validationError = isGhostInvalid
    ? 'Zeitüberschneidung – Termin überlappt einen anderen oder liegt außerhalb der Arbeitszeit.'
    : !parsed.success
    ? (parsed.error.issues[0]?.message ?? 'Daten unvollständig')
    : null

  return (
    <>
      <DraftAppointmentBar
        workOrderId={draft.workOrderId}
        technicianName={technicianName}
        workDateTime={workDateTime}
        canSave={canSave}
        isPending={isPending}
        mode={mode}
        collisionError={isGhostInvalid}
        backendError={backendError}
        onOpenModal={() => setShowModal(true)}
        onSave={onSave}
        onCancel={onCancel}
      />

      <DraftAppointmentForm
        show={showModal}
        setShow={setShowModal}
        draft={draftAsDto}
        technicianName={technicianName}
        canSave={canSave}
        isPending={isPending}
        mode={mode}
        collisionWarning={!isFullDay && collisionWarning}
        validationError={validationError}
        isFullDay={isFullDay}
        onFullDayChange={(v) => {
          setFullDay(v, {
            shiftStart: technicianShift?.shiftStart ?? null,
            shiftEnd: technicianShift?.shiftEnd ?? null,
          })
          if (v && ghostTechnicianId) deleteBreakForTechnician(ghostTechnicianId)
        }}
        onDurationsChange={tryDurationsChange}
        onPlannedStartChange={updatePosition}
        onTradeCodeChange={updateTradeCode}
        description={draft.description}
        onDescriptionChange={(v) => updateDescription(v || null)}
        onSave={onSave}
        files={files}
        onFilesChange={onFilesChange}
        existingAttachments={existingAttachments}
        onRemoveExistingAttachment={onRemoveExistingAttachment}
        backendError={backendError}
      />
    </>
  )
}
